import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { registerRequest, editUserRequest, getUsuariosByEmpresaRequest, deleteUserRequest } from '../services/auth/authData';
import {
  Typography,
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Container,
  Paper,
  TableContainer,
  TablePagination,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { toast } from 'react-toastify';

const EmpleadoModal = ({
  open,
  onClose,
  onSubmit,
  initialData = { usu_nombre: '', usu_apellido: '', usu_email: '', usu_password: '' },
  isEdit = false,
}) => {
  const [form, setForm] = useState(initialData);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [nameError, setNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');

  useEffect(() => {
    setForm(initialData);
    setEmailError('');
    setPasswordError('');
    setNameError('');
    setLastNameError('');
  }, [initialData, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    if (name === 'usu_nombre') {
      setNameError(value.trim() ? '' : 'El nombre es obligatorio');
    }
    if (name === 'usu_apellido') {
      setLastNameError(value.trim() ? '' : 'El apellido es obligatorio');
    }
    if (name === 'usu_email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setEmailError(emailRegex.test(value) ? '' : 'Correo electrónico inválido');
    }
    if (name === 'usu_password' && !isEdit) {
      setPasswordError(value.length >= 6 ? '' : 'La contraseña debe tener al menos 6 caracteres');
    }
  };

  const handleSubmit = () => {
    if (nameError || lastNameError || emailError || passwordError) {
      toast.error('Por favor corrige los errores del formulario.');
      return;
    }
    if (
      !form.usu_nombre.trim() ||
      !form.usu_apellido.trim() ||
      !form.usu_email.trim() ||
      (!isEdit && !form.usu_password.trim())
    ) {
      toast.error('Todos los campos son obligatorios.');
      return;
    }
    onSubmit(form);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, color: '#001F3F' }}>
        {isEdit ? 'Editar empleado' : 'Crear empleado'}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <TextField
          margin="dense"
          label="Nombre"
          name="usu_nombre"
          value={form.usu_nombre}
          onChange={handleChange}
          fullWidth
          error={!!nameError}
          helperText={nameError}
        />
        <TextField
          margin="dense"
          label="Apellido"
          name="usu_apellido"
          value={form.usu_apellido}
          onChange={handleChange}
          fullWidth
          error={!!lastNameError}
          helperText={lastNameError}
        />
        <TextField
          margin="dense"
          label="Correo electrónico"
          name="usu_email"
          value={form.usu_email}
          onChange={handleChange}
          fullWidth
          error={!!emailError}
          helperText={emailError}
        />
        {!isEdit && (
          <TextField
            margin="dense"
            label="Contraseña"
            name="usu_password"
            type="password"
            value={form.usu_password}
            onChange={handleChange}
            fullWidth
            error={!!passwordError}
            helperText={passwordError || 'Mínimo 6 caracteres'}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ color: '#475569' }}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!!nameError || !!lastNameError || !!emailError || (!!passwordError && !isEdit)}
          sx={{
            backgroundColor: '#001F3F',
            color: '#fff',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#003366' },
          }}
        >
          {isEdit ? 'Actualizar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const GestionEmpleados = () => {
  const [empleados, setEmpleados] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editEmpleado, setEditEmpleado] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const { user, loading } = useAuth();
  // Traer solo empleados de la empresa del usuario actual
  const fetchEmpleados = async (emp_codigo) => {
    try {
      const empleadosEmpresa = await getUsuariosByEmpresaRequest(emp_codigo);
      setEmpleados(Array.isArray(empleadosEmpresa) ? empleadosEmpresa : []);
    } catch (error) {
      setEmpleados([]);
      toast.error('Error al cargar los empleados.');
    }
  };
  
  useEffect(() => {
    if (!loading && user?.emp_codigo) {
      fetchEmpleados(user.emp_codigo);
    }
    // eslint-disable-next-line
  }, [loading, user]);

  if (loading || !user?.emp_codigo) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Typography variant="body2" sx={{ color: '#6b7280' }}>
          Cargando empleados...
        </Typography>
      </Box>
    );
  }

  const handleCreate = () => {
    setEditEmpleado(null);
    setModalOpen(true);
  };

  const handleEdit = (empleado) => {
    setEditEmpleado(empleado);
    setModalOpen(true);
  };

  const handleModalSubmit = async (data) => {
    try {
      if (editEmpleado) {
        // Si no hay ningún cambio y no se ha escrito contraseña nueva, no llamamos al backend
        const sinCambios =
          editEmpleado.usu_nombre === data.usu_nombre &&
          editEmpleado.usu_apellido === data.usu_apellido &&
          editEmpleado.usu_email === data.usu_email &&
          (!data.usu_password || data.usu_password.trim() === '');

        if (sinCambios) {
          toast.info('No hay cambios para guardar.');
          setModalOpen(false);
          return;
        }

        // Para edición, mandamos campos permitidos y nunca enviamos undefined al backend
        const payloadEdit = {
          usu_nombre: data.usu_nombre,
          usu_apellido: data.usu_apellido,
          usu_email: data.usu_email,
        };

        if (data.usu_password && data.usu_password.trim().length >= 6) {
          payloadEdit.usu_password = data.usu_password.trim();
        }

        await editUserRequest(editEmpleado.usu_codigo, payloadEdit);
        toast.success('Empleado actualizado correctamente.');
      } else {
        const payload = {
          ...data,
          emp_codigo: user.emp_codigo,
          usu_rol: "Usuario",
        };
        await registerRequest(payload);
        toast.success('Empleado creado correctamente.');
      }
      setModalOpen(false);
      fetchEmpleados(user.emp_codigo);
    } catch (error) {
      const mensaje =
        error?.response?.data?.message ||
        error?.message ||
        'Error al guardar el empleado.';
      toast.error(mensaje);
    }
  };

  const handleConfirmDelete = (empleado) => {
    setDeleteId(empleado.usu_codigo);
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    try {
      await deleteUserRequest(deleteId);
      setConfirmOpen(false);
      fetchEmpleados(user.emp_codigo);
      toast.success('Empleado eliminado correctamente.');
    } catch (error) {
      const mensaje =
        error?.response?.data?.message ||
        error?.message ||
        'Error al eliminar el empleado.';
      toast.error(mensaje);
      setConfirmOpen(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Paper
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#001F3F' }}>
              Gestión de empleados
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
              Administra las cuentas de los usuarios de tu organización.
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleCreate}
            startIcon={<PersonAddIcon />}
            sx={{
              backgroundColor: '#001F3F',
              color: '#fff',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#003366' },
            }}
          >
            Crear empleado
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Apellido</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Correo</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#001F3F' }} align="center">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {empleados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      No hay empleados registrados.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                empleados
                  .filter((e) => e.usu_codigo !== user.usu_codigo)
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((empleado) => (
                    <TableRow key={empleado.usu_codigo} hover>
                      <TableCell>{empleado.usu_nombre}</TableCell>
                      <TableCell>{empleado.usu_apellido}</TableCell>
                      <TableCell>{empleado.usu_email}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton
                            onClick={() => handleEdit(empleado)}
                            sx={{
                              color: '#4b5563',
                              '&:hover': { backgroundColor: '#e5e7eb' },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            onClick={() => handleConfirmDelete(empleado)}
                            sx={{
                              color: '#d32f2f',
                              '&:hover': { backgroundColor: '#fee2e2' },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={empleados.filter((e) => e.usu_codigo !== user.usu_codigo).length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
        />

        {/* Modal para crear/editar */}
        <EmpleadoModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSubmit={handleModalSubmit}
          initialData={editEmpleado || { usu_nombre: '', usu_apellido: '', usu_email: '', usu_password: '' }}
          isEdit={!!editEmpleado}
        />

        {/* Confirmación de eliminación */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 600, color: '#001F3F' }}>
            Eliminar empleado
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: '#4b5563', mb: 1.5 }}>
              ¿Estás seguro de que deseas eliminar este empleado?
            </Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: '0.85rem' }}>
              Esta acción no se puede deshacer y eliminará las credenciales de acceso del usuario.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setConfirmOpen(false)}
              sx={{ color: '#475569', textTransform: 'none' }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              variant="contained"
              sx={{
                backgroundColor: '#d32f2f',
                color: '#fff',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#b91c1c' },
              }}
            >
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};